import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  InputBase,
  Popover,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  DateRangeRounded,
  ExpandMoreRounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { formatDate } from "./clientFormatViewClient";

// Selector de fechas de las gráficas de cliente: atajos (hoy, ayer, últimos
// días, en lo que va de mes, trimestre o año, toda la campaña, N días hasta hoy
// o hasta ayer) y un calendario para elegir el rango exacto. Se aplica al
// pulsar "Aplicar"; "Cancelar" deja el que había.

const DAY = "YYYY-MM-DD";

export type RangePreset =
  | "custom"
  | "today"
  | "yesterday"
  | "last7"
  | "last14"
  | "last30"
  | "monthToDate"
  | "quarterToDate"
  | "yearToDate"
  | "lifetime"
  | "daysToToday"
  | "daysToYesterday";

/** Rango de fechas (YYYY-MM-DD, ambos incluidos) y cómo se eligió. */
export interface DateRange {
  preset: RangePreset;
  from: string;
  to: string;
  /** Días de "N días hasta hoy / hasta ayer". */
  days?: number;
}

/** Límites de lo que se puede elegir: desde el primer dato hasta el final. */
export interface RangeBounds {
  min: string;
  max: string;
}

export const LIFETIME: DateRange = { preset: "lifetime", from: "", to: "" };

/**
 * El rango de un atajo hoy. `yesterday` es el último día con datos. "Toda la
 * campaña" son los límites.
 */
export const presetRange = (
  preset: RangePreset,
  bounds: RangeBounds,
  yesterday: string,
  days = 30
): { from: string; to: string } => {
  const today = dayjs().format(DAY);
  const last = (n: number, end: string) => ({
    from: dayjs(end)
      .subtract(n - 1, "day")
      .format(DAY),
    to: end,
  });
  const quarterStart = dayjs()
    .month(Math.floor(dayjs().month() / 3) * 3)
    .startOf("month");
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday":
      return { from: yesterday, to: yesterday };
    case "last7":
      return last(7, yesterday);
    case "last14":
      return last(14, yesterday);
    case "last30":
      return last(30, yesterday);
    case "monthToDate":
      return { from: dayjs().startOf("month").format(DAY), to: today };
    case "quarterToDate":
      return { from: quarterStart.format(DAY), to: today };
    case "yearToDate":
      return { from: dayjs().startOf("year").format(DAY), to: today };
    case "daysToToday":
      return last(Math.max(1, days), today);
    case "daysToYesterday":
      return last(Math.max(1, days), yesterday);
    default:
      return { from: bounds.min, to: bounds.max };
  }
};

/**
 * Las fechas de un rango: los atajos se recalculan (cambian con el día y, el de
 * toda la campaña, con lo que se está viendo); el elegido a mano se queda.
 */
export const resolveRange = (
  range: DateRange,
  bounds: RangeBounds,
  yesterday: string
): { from: string; to: string } =>
  range.preset === "custom"
    ? { from: range.from, to: range.to }
    : presetRange(range.preset, bounds, yesterday, range.days);

/** Todos los días del rango, en orden. */
export const rangeDays = (from: string, to: string): string[] => {
  const days: string[] = [];
  for (
    let d = dayjs(from);
    !d.isAfter(dayjs(to)) && days.length < 3700;
    d = d.add(1, "day")
  )
    days.push(d.format(DAY));
  return days;
};

const PRESETS: RangePreset[] = [
  "today",
  "yesterday",
  "last7",
  "last14",
  "last30",
  "monthToDate",
  "quarterToDate",
  "yearToDate",
  "lifetime",
];

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

/**
 * Un mes del calendario, con su cabecera, para el scroll de meses. El rango va
 * de fondo de lado a lado (también detrás del primer y el último día); el
 * inicio y el final de la campaña llevan una raya; los días ya pasados, en
 * negrita: los que vienen se pueden elegir, pero importan menos.
 */
const MonthGrid: React.FC<{
  month: dayjs.Dayjs;
  from: string;
  to: string;
  bounds: RangeBounds;
  yesterday: string;
  onPick: (day: string) => void;
}> = ({ month, from, to, bounds, yesterday, onPick }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const band = alpha(theme.palette.primary.main, 0.14);
  const mark = theme.palette.primary.main;
  const first = month.startOf("month");
  const cells: (string | null)[] = [
    ...Array(first.day()).fill(null),
    ...Array.from({ length: first.daysInMonth() }, (_, i) =>
      first.add(i, "day").format(DAY)
    ),
  ];
  const today = dayjs().format(DAY);
  return (
    <Box data-month={month.format("YYYY-MM")} sx={{ mb: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          px: 1,
          py: 0.75,
        }}
      >
        {new Intl.DateTimeFormat(i18n.language, {
          month: "short",
          year: "numeric",
        })
          .format(first.toDate())
          .replace(".", "")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 36px)" }}>
        {cells.map((day, index) => {
          if (!day) return <span key={`empty-${index}`} />;
          const disabled = day < bounds.min || day > bounds.max;
          const inRange = Boolean(from && to && day >= from && day <= to);
          const edge = day === from || day === to;
          const past = day <= yesterday;
          const start = day === bounds.min;
          const end = day === bounds.max;
          // El fondo del rango sigue por detrás del círculo del primer y el
          // último día: media celda hacia dentro del rango.
          const background =
            !inRange || from === to
              ? "none"
              : day === from
                ? `linear-gradient(to right, transparent 50%, ${band} 50%)`
                : day === to
                  ? `linear-gradient(to left, transparent 50%, ${band} 50%)`
                  : `linear-gradient(${band}, ${band})`;
          return (
            <ButtonBase
              key={day}
              disabled={disabled}
              onClick={() => onPick(day)}
              aria-label={day}
              aria-pressed={edge}
              title={
                start
                  ? t("clientView.range.campaignStart")
                  : end
                    ? t("clientView.range.campaignEnd")
                    : undefined
              }
              sx={{
                height: 36,
                fontSize: 13,
                fontWeight: past ? 700 : 400,
                color: disabled
                  ? "text.disabled"
                  : edge
                    ? theme.palette.primary.contrastText
                    : past
                      ? "text.primary"
                      : "text.secondary",
                backgroundImage: background,
                position: "relative",
                // Raya al inicio (izquierda) y al final (derecha) de la campaña.
                "&::after":
                  start || end
                    ? {
                        content: '""',
                        position: "absolute",
                        top: 6,
                        bottom: 6,
                        [start ? "left" : "right"]: 0,
                        width: 3,
                        borderRadius: 2,
                        backgroundColor: mark,
                        zIndex: 2,
                      }
                    : undefined,
                "&::before": edge
                  ? {
                      content: '""',
                      position: "absolute",
                      inset: 2,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      zIndex: 0,
                    }
                  : day === today
                    ? {
                        content: '""',
                        position: "absolute",
                        inset: 2,
                        borderRadius: "50%",
                        border: `1px solid ${theme.palette.divider}`,
                      }
                    : undefined,
                "& span": { position: "relative", zIndex: 1 },
                "&:hover:not(:disabled)": {
                  backgroundColor: inRange
                    ? "transparent"
                    : theme.palette.neutral[100],
                  "&::before": edge
                    ? undefined
                    : {
                        content: '""',
                        position: "absolute",
                        inset: 2,
                        borderRadius: "50%",
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.18
                        ),
                      },
                },
              }}
            >
              <span>{Number(day.slice(8))}</span>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

/** "2026-10-29" → "29/10/26": como se escribe y se lee la fecha a mano. */
export const toShortDate = (value: string): string =>
  value ? dayjs(value).format("DD/MM/YY") : "";

/**
 * "29/10/26" (o 29/10/2026, 1/9/26) → "2026-10-29". null si no es una fecha
 * de verdad (31/02, letras...).
 */
export const parseShortDate = (text: string): string | null => {
  const match = text
    .trim()
    .match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (!match) return null;
  const [, d, m, y] = match;
  const year = y.length === 2 ? 2000 + Number(y) : Number(y);
  const date = dayjs(
    `${year}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`,
    DAY
  );
  // dayjs acepta el 31/02 pasándolo a marzo: si no coincide, no vale.
  return date.isValid() &&
    date.date() === Number(d) &&
    date.month() + 1 === Number(m)
    ? date.format(DAY)
    : null;
};

/**
 * Fecha escrita a mano, siempre como dd/mm/aa (el selector nativo de fecha
 * cambia de formato según el navegador). Se aplica al salir o con Enter; una
 * fecha que no vale vuelve a la que había y una fuera de la campaña se lleva
 * al borde más cercano.
 */
const DateField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  bounds: RangeBounds;
}> = ({ label, value, onChange, bounds }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [text, setText] = useState(toShortDate(value));
  // Lo que se elige en el calendario también se ve aquí.
  useEffect(() => setText(toShortDate(value)), [value]);
  const commit = () => {
    const parsed = parseShortDate(text);
    if (!parsed) {
      setText(toShortDate(value));
      return;
    }
    const clamped =
      parsed < bounds.min
        ? bounds.min
        : parsed > bounds.max
          ? bounds.max
          : parsed;
    setText(toShortDate(clamped));
    if (clamped !== value) onChange(clamped);
  };
  return (
    <Box
      component="label"
      sx={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "10px",
        px: 1.25,
        py: 0.5,
        minWidth: 0,
        "&:focus-within": { borderColor: theme.palette.primary.main },
      }}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
        {label}
      </Typography>
      <InputBase
        value={text}
        placeholder={t("clientView.range.datePlaceholder")}
        inputProps={{ inputMode: "numeric", "aria-label": label }}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
        }}
        sx={{ fontSize: 13, "& input": { p: 0 } }}
      />
    </Box>
  );
};

/**
 * Botón con el rango elegido y, al pulsarlo, el selector. Las flechas de los
 * lados mueven el mismo número de días hacia atrás o hacia delante.
 */
const DateRangePickerViewClient: React.FC<{
  value: DateRange;
  onChange: (range: DateRange) => void;
  bounds: RangeBounds;
  /** Último día con datos (ayer). */
  yesterday: string;
}> = ({ value, onChange, bounds, yesterday }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = i18n.language;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<DateRange>(value);
  // Primer clic: el inicio; segundo: el final.
  const [picking, setPicking] = useState<"from" | "to">("from");
  const [days, setDays] = useState({ toToday: 30, toYesterday: 30 });
  const scroller = useRef<HTMLDivElement>(null);

  const applied = resolveRange(value, bounds, yesterday);
  const draftDates = resolveRange(draft, bounds, yesterday);

  const months = useMemo(() => {
    const list: dayjs.Dayjs[] = [];
    const last = dayjs(
      bounds.max > dayjs().format(DAY) ? bounds.max : dayjs().format(DAY)
    ).startOf("month");
    for (
      let m = dayjs(bounds.min).startOf("month");
      !m.isAfter(last) && list.length < 120;
      m = m.add(1, "month")
    )
      list.push(m);
    return list;
  }, [bounds.min, bounds.max]);

  // Al abrir, el calendario va al mes del inicio del rango.
  useEffect(() => {
    if (!anchor) return;
    const month = (draftDates.from || bounds.min).slice(0, 7);
    requestAnimationFrame(() =>
      scroller.current
        ?.querySelector(`[data-month="${month}"]`)
        ?.scrollIntoView({ block: "start" })
    );
    // Solo al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setDraft(value);
    setPicking("from");
    setAnchor(event.currentTarget);
  };
  const close = () => setAnchor(null);
  const apply = () => {
    onChange(
      draft.preset === "custom"
        ? { ...draft, ...draftDates }
        : { ...draft, from: draftDates.from, to: draftDates.to }
    );
    close();
  };

  const pickDay = (day: string) => {
    if (picking === "from" || !draftDates.from) {
      setDraft({ preset: "custom", from: day, to: day });
      setPicking("to");
      return;
    }
    const [from, to] =
      day < draftDates.from ? [day, draftDates.from] : [draftDates.from, day];
    setDraft({ preset: "custom", from, to });
    setPicking("from");
  };

  const scrollMonth = (direction: 1 | -1) => {
    const box = scroller.current;
    if (box) box.scrollBy({ top: direction * 260, behavior: "smooth" });
  };

  const presetLabel = (preset: RangePreset) =>
    t(`clientView.range.${preset}`, { count: value.days });
  const label = `${formatDate(applied.from, language)} – ${formatDate(applied.to, language)}`;

  const presetItem = (preset: RangePreset) => (
    <ButtonBase
      key={preset}
      onClick={() => setDraft({ preset, from: "", to: "" })}
      sx={{
        justifyContent: "flex-start",
        px: 2,
        py: 1.1,
        fontSize: 14,
        width: "100%",
        textAlign: "left",
        backgroundColor:
          draft.preset === preset
            ? alpha(theme.palette.primary.main, 0.1)
            : "transparent",
        "&:hover": { backgroundColor: theme.palette.neutral[100] },
      }}
    >
      {t(`clientView.range.${preset}`)}
    </ButtonBase>
  );

  const daysItem = (preset: "daysToToday" | "daysToYesterday") => {
    const key = preset === "daysToToday" ? "toToday" : "toYesterday";
    const select = () =>
      setDraft({ preset, from: "", to: "", days: days[key] });
    return (
      <Box
        key={preset}
        role="button"
        tabIndex={0}
        aria-pressed={draft.preset === preset}
        onClick={select}
        onKeyDown={(event) => event.key === "Enter" && select()}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 0.75,
          cursor: "pointer",
          backgroundColor:
            draft.preset === preset
              ? alpha(theme.palette.primary.main, 0.1)
              : "transparent",
          "&:hover": {
            backgroundColor:
              draft.preset === preset
                ? alpha(theme.palette.primary.main, 0.14)
                : theme.palette.neutral[100],
          },
        }}
      >
        <InputBase
          type="number"
          value={days[key]}
          inputProps={{
            min: 1,
            max: 3650,
            "aria-label": t(`clientView.range.${preset}Suffix`),
          }}
          onFocus={select}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            const n = Math.max(1, Math.min(3650, Number(event.target.value)));
            setDays((prev) => ({ ...prev, [key]: n }));
            setDraft({ preset, from: "", to: "", days: n });
          }}
          sx={{
            width: 52,
            fontSize: 14,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            px: 1,
            py: 0.25,
          }}
        />
        <Typography sx={{ fontSize: 14 }}>
          {t(`clientView.range.${preset}Suffix`)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <ButtonBase
        onClick={open}
        aria-haspopup="dialog"
        aria-label={t("clientView.range.pick")}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          height: 36,
          px: 1.5,
          borderRadius: 999,
          border: `1px solid ${theme.palette.divider}`,
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        <DateRangeRounded sx={{ fontSize: 18, color: "text.secondary" }} />
        {value.preset !== "custom" && (
          <Box component="span" sx={{ color: "text.secondary" }}>
            {presetLabel(value.preset)} ·
          </Box>
        )}
        {label}
        <ExpandMoreRounded sx={{ fontSize: 18, color: "text.secondary" }} />
      </ButtonBase>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "16px",
              overflow: "hidden",
              maxWidth: "calc(100vw - 32px)",
            },
          },
        }}
      >
        <Box
          role="dialog"
          aria-label={t("clientView.range.pick")}
          sx={{ display: "flex", flexWrap: { xs: "wrap", sm: "nowrap" } }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 210 },
              borderRight: { sm: `1px solid ${theme.palette.divider}` },
              py: 1,
            }}
          >
            {presetItem("custom")}
            <Divider sx={{ my: 1 }} />
            {PRESETS.map(presetItem)}
            <Divider sx={{ my: 1 }} />
            {daysItem("daysToToday")}
            {daysItem("daysToYesterday")}
          </Box>
          <Box sx={{ p: 2, width: { xs: "100%", sm: "auto" } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
              }}
            >
              <DateField
                label={t("clientView.range.start")}
                value={draftDates.from}
                bounds={bounds}
                onChange={(from) =>
                  from &&
                  setDraft({
                    preset: "custom",
                    from,
                    to: from > draftDates.to ? from : draftDates.to,
                  })
                }
              />
              <span>—</span>
              <DateField
                label={t("clientView.range.end")}
                value={draftDates.to}
                bounds={bounds}
                onChange={(to) =>
                  to &&
                  setDraft({
                    preset: "custom",
                    from: to < draftDates.from ? to : draftDates.from,
                    to,
                  })
                }
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5,
                mb: 0.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() => scrollMonth(-1)}
                aria-label={t("clientView.range.previousMonth")}
              >
                <ChevronLeftRounded />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => scrollMonth(1)}
                aria-label={t("clientView.range.nextMonth")}
              >
                <ChevronRightRounded />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 36px)",
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 0.5,
              }}
            >
              {WEEKDAYS.map((weekday) => (
                <Typography
                  key={weekday}
                  sx={{
                    fontSize: 11,
                    color: "text.secondary",
                    textAlign: "center",
                  }}
                >
                  {new Intl.DateTimeFormat(language, {
                    weekday: "narrow",
                  }).format(dayjs("2023-01-01").add(weekday, "day").toDate())}
                </Typography>
              ))}
            </Box>
            <Box
              ref={scroller}
              sx={{ height: 300, overflowY: "auto", pt: 0.5 }}
            >
              {months.map((month) => (
                <MonthGrid
                  key={month.format("YYYY-MM")}
                  month={month}
                  from={draftDates.from}
                  to={draftDates.to}
                  bounds={bounds}
                  yesterday={yesterday}
                  onPick={pickDay}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1.5,
              }}
            >
              <Button onClick={close} sx={{ textTransform: "none" }}>
                {t("clientView.range.cancel")}
              </Button>
              <Button
                onClick={apply}
                variant="contained"
                disableElevation
                disabled={!draftDates.from || !draftDates.to}
                sx={{ textTransform: "none", borderRadius: 999 }}
              >
                {t("clientView.range.apply")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangePickerViewClient;

/**
 * Barra de fechas de arriba de una página: el selector y cuántos días se ven.
 * Todo lo de la página sigue a estas fechas.
 */
export const DateRangeBar: React.FC<{
  value: DateRange;
  onChange: (range: DateRange) => void;
  bounds: RangeBounds;
  yesterday: string;
}> = ({ value, onChange, bounds, yesterday }) => {
  const { t } = useTranslation();
  const { from, to } = resolveRange(value, bounds, yesterday);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        {t("clientView.range.daysShown", { count: rangeDays(from, to).length })}
      </Typography>
      <DateRangePickerViewClient
        value={value}
        onChange={onChange}
        bounds={bounds}
        yesterday={yesterday}
      />
    </Box>
  );
};
